import React, { useState, useEffect, useRef } from 'react';
import './styles/tokens.css';
import './styles/shared.css';
import './App.css';

import { auth, demandes as demandesApi } from './services/api';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ChangePassword from './components/ChangePassword';
import TwoFactorSetup from './components/TwoFactorSetup';
import TwoFactorVerify from './components/TwoFactorVerify';
import SignaturePublic from './components/SignaturePublic';
import PortailClient from './components/PortailClient';
import PortailConstructeur from './components/PortailConstructeur';

// Initialiser le thème avant le premier rendu
document.documentElement.setAttribute('data-theme', localStorage.getItem('crm-dark') === '1' ? 'dark' : 'light');

// ── Routes publiques — rendu hors du composant principal (pas d'auth) ──
const signerMatch = window.location.pathname.match(/^\/signer\/(.+)$/);
const priseRdvMatch = window.location.pathname.match(/^\/prise-rdv\/(.+)$/);
// Portail client d'un dossier commercial : le client dépose ses pièces depuis
// un lien reçu de sa concession. Aucune authentification CRM.
const monDossierMatch = window.location.pathname.match(/^\/mon-dossier\/(.+)$/);

const IS_DEV = window.location.hostname !== 'autodom97.fr';

function DevBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (!IS_DEV || dismissed) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: '#dc2626', color: '#fff',
      padding: '6px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
      fontSize: '13px', fontWeight: '600', lineHeight: '1.4',
      boxShadow: '0 2px 8px rgba(220,38,38,0.4)',
    }}>
      <span>⚠️ ENVIRONNEMENT DE DÉVELOPPEMENT — Vous n'êtes pas sur la production</span>
      <a
        href="https://autodom97.fr"
        style={{ color: '#fff', textDecoration: 'underline', fontWeight: '700', whiteSpace: 'nowrap' }}
      >
        Aller sur la prod →
      </a>
      <button
        onClick={() => setDismissed(true)}
        title="Masquer"
        style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
          borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer',
          fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canInstall, setCanInstall] = useState(!!window.__pwaPrompt);
  const [pendingState, setPendingState] = useState(null); // 'verify' | 'setup' | null
  const [tempToken, setTempToken] = useState(null);
  const [ipBlocked, setIpBlocked] = useState(false);
  const [authError, setAuthError] = useState('');

  // ── Retour de redirection Microsoft (/oidc/callback) ──
  const isOidcCallback = window.location.pathname === '/oidc/callback';
  // Le `code` Microsoft est à usage unique (et le sid PKCE est supprimé au 1er échange).
  // StrictMode (dev) monte l'effet 2×, ce qui déclencherait un 2e callback voué à échouer
  // (« Session expirée »). Ce garde-fou garantit un seul échange.
  const oidcExchanged = useRef(false);

  useEffect(() => {
    if (isOidcCallback) {
      if (!oidcExchanged.current) {
        oidcExchanged.current = true;
        handleOidcCallback();
      }
      return;
    }
    checkAuth();
    window.__pwaPromptCallback = () => setCanInstall(true);
    const handleIpBlocked = () => setIpBlocked(true);
    window.addEventListener('ip-blocked', handleIpBlocked);
    return () => {
      window.__pwaPromptCallback = null;
      window.removeEventListener('ip-blocked', handleIpBlocked);
    };
  }, []);

  const handleInstallPWA = async () => {
    const prompt = window.__pwaPrompt;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      window.__pwaPrompt = null;
      setCanInstall(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await auth.getMe();
        setUser(response.data.user);
      } catch (error) {
        console.error('Auth error:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  // Échange le `code` Microsoft reçu sur /oidc/callback contre le JWT du CRM.
  const handleOidcCallback = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error_description') || params.get('error');

    // Nettoie l'URL (retire code/state) quel que soit le résultat.
    const clearUrl = () => window.history.replaceState({}, '', '/');

    try {
      if (oauthError) throw new Error(oauthError);
      if (!code || !state) throw new Error('Réponse Microsoft incomplète');
      const response = await auth.microsoftCallback(code, state);
      clearUrl();
      handleFullLogin(response.data.user, response.data.token);
    } catch (error) {
      clearUrl();
      setAuthError(error.response?.data?.error || error.message || 'Échec de la connexion Microsoft');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (username, password) => {
    try {
      const response = await auth.login(username, password);
      if (response.data.requires_2fa) {
        setTempToken(response.data.temp_token);
        setPendingState('verify');
        return { success: true };
      }
      if (response.data.requires_2fa_setup) {
        setTempToken(response.data.temp_token);
        setPendingState('setup');
        return { success: true };
      }
      handleFullLogin(response.data.user, response.data.token);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur de connexion'
      };
    }
  };

  const handleFullLogin = (userData, token) => {
    if (token) localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setTempToken(null);
    setPendingState(null);
    setUser(userData);
  };

  const resetAuth = () => {
    setTempToken(null);
    setPendingState(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setTempToken(null);
    setPendingState(null);
    setUser(null);
  };

  // Page publique de signature — pas de login requis
  if (signerMatch) {
    return <SignaturePublic token={signerMatch[1]} />;
  }

  // Portail client — pas de login requis : le lien et le code SMS font foi.
  if (monDossierMatch) {
    return <PortailClient token={monDossierMatch[1]} />;
  }

  // Prise de RDV en ligne : le module est désormais une app autonome servie sur un
  // sous-domaine dédié (prise-rdv/). On redirige les anciens liens /prise-rdv/:slug.
  if (priseRdvMatch) {
    const rdvBase = process.env.REACT_APP_RDV_URL || 'https://rdv.autodom97.fr';
    window.location.replace(`${rdvBase}/?slug=${encodeURIComponent(priseRdvMatch[1])}`);
    return null;
  }

  // Portail constructeur — accès externe dédié
  if (window.location.pathname.startsWith('/portail-constructeur')) {
    return <PortailConstructeur />;
  }

  if (ipBlocked) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#FAF9F5', padding: '20px'
      }}>
        <div style={{
          background: '#fff', borderRadius: '16px', padding: '48px 40px', maxWidth: '440px',
          textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 12px' }}>
            Accès refusé
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', margin: '0 0 24px' }}>
            Votre adresse IP n'est pas autorisée à accéder au CRM.<br />
            Contactez votre administrateur pour obtenir l'accès.
          </p>
          <button
            onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.reload(); }}
            style={{
              padding: '10px 24px', background: '#e2e8f0', color: '#334155', border: 'none',
              borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="App" style={IS_DEV ? { paddingTop: '34px' } : undefined}>
      <DevBanner />
      {pendingState === 'setup' && (
        <TwoFactorSetup tempToken={tempToken} onSuccess={(u) => handleFullLogin(u)} />
      )}
      {pendingState === 'verify' && (
        <TwoFactorVerify tempToken={tempToken} onSuccess={(u) => handleFullLogin(u)} onBack={resetAuth} />
      )}
      {!pendingState && (user ? (
        user.must_change_password ? (
          <ChangePassword user={user} onSuccess={(updatedUser) => setUser(updatedUser)} />
        ) : (
          <Dashboard user={user} onLogout={handleLogout} />
        )
      ) : (
        <Login onLogin={handleLogin} authError={authError} />
      ))}

      {canInstall && (
        <button
          onClick={handleInstallPWA}
          title="Installer l'application sur votre bureau"
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '999px',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37,99,235,0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 9999,
          }}
        >
          <span style={{ fontSize: '18px' }}>⬇️</span>
          Installer l'app
        </button>
      )}
    </div>
  );
}

export default App;
